import React from 'react';
import { useHistory } from 'react-router-dom';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import css from './CategoryShortcuts.module.css';

const PRIMARY_CATEGORIES = [
  {
    desktopLabel: 'Boat Rentals',
    mobileLabel: 'Boat Rentals',
    image: 'https://images.unsplash.com/photo-1520255870062-bd79d3865de7?q=80&w=1746&auto=format&fit=crop',
    pubCategoryLevel1: 'BoatRentals',
  },
  {
    desktopLabel: 'Private Charters',
    mobileLabel: 'Private Charters',
    image: 'https://images.unsplash.com/photo-1708246117134-9b3b72fd4e76?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTV8fGdyb3VwJTIwY2hhcnRlcnxlbnwwfHwwfHx8MA%3D%3D',
    pubCategoryLevel1: 'private_charters',
  },
  {
    desktopLabel: 'Experiences',
    mobileLabel: 'Experiences',
    image: 'https://images.unsplash.com/photo-1742668751915-942e80473e67?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cGFkZGxld2hlZWxlcnxlbnwwfHwwfHx8MA%3D%3D',
    pubCategoryLevel1: 'has_any:ticketed_charters,ticketed_experiences,private_experiences',
  },
];

const SECONDARY_CATEGORIES = [
  { desktopLabel: 'Captains & Crew',       mobileLabel: 'Captains & Crew',          pubCategoryLevel1: 'captains',                                          image: 'https://images.unsplash.com/photo-1623946682527-da72b9d3bf2c?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8Ym9hdCUyMGNhcHRhaW58ZW58MHx8MHx8fDA%3D' },
  { desktopLabel: 'Launch & Delivery',     mobileLabel: 'Launch & Delivery', pubCategoryLevel1: 'dock-ramp-delivery',                                   image: 'https://images.unsplash.com/photo-1711963069605-dbf66e42c5c0?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8dHJ1Y2slMjB0b3dpbmclMjBib2F0fGVufDB8fDB8fHww' },
  { desktopLabel: 'Moorage & Storage',     mobileLabel: 'Moorage & Storage', pubCategoryLevel1: 'moorage-storage-parking',                              image: 'https://images.unsplash.com/photo-1547205725-2a24bf99d0b8?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTR8fG1hcmluYXxlbnwwfHwwfHx8MA%3D%3D' },
  { desktopLabel: 'Marine Services',       mobileLabel: 'Services',          pubCategoryLevel1: 'has_any:mechanics-and-technicians,marine-services',    image: 'https://plus.unsplash.com/premium_photo-1663011418894-f00a839066e8?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8OXx8Ym9hdCUyMG1lY2hhbmljfGVufDB8fDB8fHww' },
  { desktopLabel: 'Paddle & Gear', mobileLabel: 'Paddle & Gear',     pubCategoryLevel1: 'accessories-boating',                                  image: 'https://plus.unsplash.com/premium_photo-1661893427047-16f6ddc173f6?w=500&auto=format&fit=crop&q=60&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MXx8a2F5YWt8ZW58MHx8MHx8fDA%3D' },
];

// All 8 categories in mobile order, with isPrimary flag for font-weight
const ALL_MOBILE_CATEGORIES = [
  ...PRIMARY_CATEGORIES.map(c => ({ ...c, isPrimary: true })),
  ...SECONDARY_CATEGORIES.map(c => ({ ...c, isPrimary: false })),
];

const CategoryShortcuts = () => {
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const handleCategoryClick = pubCategoryLevel1 => {
    const to = createResourceLocatorString(
      'SearchPage',
      routeConfiguration,
      {},
      { pub_categoryLevel1: pubCategoryLevel1 }
    );
    history.push(to);
  };

  return (
    <section className={css.root}>
      <div className={css.container}>

        {/* Mobile: image card grid (all 8 categories, 2 columns) */}
        <div className={css.mobileGrid}>
          {ALL_MOBILE_CATEGORIES.map(({ mobileLabel, pubCategoryLevel1, isPrimary, image }) => (
            <button
              key={pubCategoryLevel1}
              type="button"
              className={isPrimary ? css.mobileCardPrimary : css.mobileCardSecondary}
              style={image ? { backgroundImage: `url(${image})` } : undefined}
              onClick={() => handleCategoryClick(pubCategoryLevel1)}
            >
              <div className={css.mobileCardOverlay} />
              <span className={css.mobileCardLabel}>{mobileLabel}</span>
            </button>
          ))}
        </div>

        {/* Desktop: image tiles (Tier 1) + pill row (Tier 2) */}
        <div className={css.desktopRows}>
          <div className={css.primaryRow}>
            {PRIMARY_CATEGORIES.map(({ desktopLabel, image, pubCategoryLevel1 }) => (
              <button
                key={pubCategoryLevel1}
                type="button"
                className={css.primaryTile}
                style={{ backgroundImage: `url(${image})` }}
                onClick={() => handleCategoryClick(pubCategoryLevel1)}
              >
                <div className={css.primaryTileOverlay} />
                <span className={css.primaryTileLabel}>{desktopLabel}</span>
              </button>
            ))}
          </div>
          <div className={css.secondaryRow}>
            {SECONDARY_CATEGORIES.map(({ desktopLabel, pubCategoryLevel1, image }) => (
              <button
                key={pubCategoryLevel1}
                type="button"
                className={css.secondaryTile}
                style={image ? { backgroundImage: `url(${image})` } : undefined}
                onClick={() => handleCategoryClick(pubCategoryLevel1)}
              >
                <div className={css.secondaryTileOverlay} />
                <span className={css.secondaryTileLabel}>{desktopLabel}</span>
              </button>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default CategoryShortcuts;
